import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LeftOutlined } from '@ant-design/icons';
import { ImagePreviewModel } from '@common/models';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';

import { useGalleryImages } from './useGalleryImages';

interface ImageButtonCardsProps {
    images: ImagePreviewModel[];
    position: number;
    onSelect: (position: number) => void;
}

const ImageButtonCards = ({ images, position, onSelect }: ImageButtonCardsProps): JSX.Element => (
    <div className={'arrow-container start'}>
        <i className={classNames('arrow left', { hide: position === 0 })} onClick={() => onSelect(position - 1)} />
        <div className="image-card-area">
            {images.map((image, index) => (
                <div
                    key={index}
                    className={classNames('image-card', { selected: index === position })}
                    onClick={() => onSelect(index)}
                >
                    <img src={image.image} />
                </div>
            ))}
        </div>
        <i
            className={classNames('arrow right', { hide: position === images.length - 1 })}
            onClick={() => onSelect(position + 1)}
        />
    </div>
);

interface ImagePreviewerProps {
    images: ImagePreviewModel[];
    position: number;
}

const ImagePrice = ({ image }: { image: ImagePreviewModel }) => {
    if (!image.unitCost && !image.unitCostWithoutDiscount) {
        return null;
    }
    const isDiscounted =
        !!image.unitCost && !!image.unitCostWithoutDiscount && image.unitCost !== image.unitCostWithoutDiscount;
    const regularPrice = isDiscounted ? (
        <span className="original-price">{formatPriceUnitOrFree(image.unitCostWithoutDiscount)}</span>
    ) : null;
    const discountPrice = (
        <span className={classNames({ 'discounted-price': isDiscounted })}>
            {formatPriceUnitOrFree(image.unitCost, image.unitOfMeasure)}
        </span>
    );
    return (
        <div className="image-info">
            {regularPrice}
            {discountPrice}
        </div>
    );
};

const ImagePreviewer = ({ images, position }: ImagePreviewerProps): JSX.Element => (
    <>
        {images
            .map((image) => {
                return (
                    <div key={image.image} className="image-preview">
                        <img src={image.image} className="img" />
                        {image.productName ? <div className="image-info">{image.productName}</div> : null}
                        <ImagePrice image={image} />
                    </div>
                );
            })
            .find((_element, index) => index === position)}
    </>
);

interface ImageGalleryProps {
    images?: ImagePreviewModel[];
    onClose: () => void;
}

const ImageGallery = ({ images: _altImages, onClose }: ImageGalleryProps): JSX.Element => {
    const { t } = useTranslation();
    const [currentImagePosition, setCurrentImagePosition] = useState(0);
    const { previewImages: images } = useGalleryImages();

    const updateCurrentImagePosition = (imagePosition: number): void => {
        if (imagePosition > 0 || imagePosition < images.length - 1) {
            setCurrentImagePosition(imagePosition);
        }
    };

    return (
        <div className="design-preview">
            <div className="return-to-design" onClick={onClose}>
                <LeftOutlined className="return-arrow" />
                {t('ORDER_SUMMARY.RETURN_TO_DESIGN')}
            </div>
            <div className={'selected-image-container arrow-container center'}>
                <i
                    className={classNames('arrow left', { hide: currentImagePosition === 0 })}
                    onClick={() => updateCurrentImagePosition(currentImagePosition - 1)}
                />
                <div className="image-area">
                    <ImagePreviewer images={images} position={currentImagePosition} />
                </div>
                <i
                    className={classNames('arrow right', { hide: currentImagePosition === images.length - 1 })}
                    onClick={() => updateCurrentImagePosition(currentImagePosition + 1)}
                />
            </div>
            <ImageButtonCards images={images} onSelect={updateCurrentImagePosition} position={currentImagePosition} />
        </div>
    );
};

export default ImageGallery;
